import _ from "underscore";
import { getLocalDaySpan, dstCalculator} from "../../utils/date";
import dayjs from "../../utils/dayjs";
import DutyStatusLog from "../../model/DutyStatusLog";
import { extend } from "../../utils/vanilla";
import pageHTML from "./hosNavigation.tmpl";
import NavigateDateMenu from "../framework/navigation";
import { handleApi } from "../../utils/error";

interface IHosDay {
    hasLogs: boolean;
    isRequestedLogsDay?: boolean;
    isUnverifiedLogsDay?: boolean;
    isMissingAddressDay?: boolean;
}

interface IHosDays {
    [dateHash: string]: IHosDay;
}

export default class HosNavigateDateMenu extends NavigateDateMenu {
    private readonly hosTemplate = _.template(pageHTML);
    private hosDays: IHosDays = {};
    private logCollection: DutyStatusLog[];
    private requestedDays: string[] = [];
    private unverifiedDays: string[] = [];
    private missingLocationDays: string[] = [];
    private requestedAddShortenYMPCLogIdSet: Set<string> = new Set();
    private historicalDateHashes: string[] = [];

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #issuesNotification": this.openDatePickerIssues
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    updateIssue(resolvedDayString: string, verifiedDay = false, resolvedEditDay = false, setAllAddressDay = false) {
        let filter = (dayString) => resolvedDayString !== dayString;
        if (verifiedDay) {
            this.unverifiedDays = this.unverifiedDays.filter(filter);
        }

        if (resolvedEditDay) {
            this.requestedDays = this.requestedDays.filter(filter);
        }

        if (setAllAddressDay) {
            this.missingLocationDays = this.missingLocationDays.filter(filter);
        }
    }

    loadLogs(logCollection: DutyStatusLog[], requestedAddShortenYMPCLogIdSet: Set<string>) {
        this.logCollection = logCollection;
        this.requestedAddShortenYMPCLogIdSet = requestedAddShortenYMPCLogIdSet;
        this.hosDays = {};
        this.populateEvents();
    }

    updateDate(date: Date) {
        let dateString = date.toISOString();
        if (this.hosDays[dateString] && this.hosDays[dateString].hasLogs) {
            super.setCurrentDay(date);
        }
    }

    render() {
        this.display(
            this.hosTemplate({
                hasUnverified: this.unverifiedDays.length > 0 && this.app.store.getState().data.ELDJurisdiction !== "EWD",
                hasRequested: this.requestedDays.length > 0,
                hasMissingLocation: this.missingLocationDays.length > 0
            })
        ).catch(handleApi);
        return this;
    }

    protected deleteDay(dateHash: string) {
        super.deleteDay(dateHash);
        delete this.hosDays[dateHash];
    }

    private getIconHtml(hosDay: IHosDay) {
        let iconString = "";
        if (hosDay.isRequestedLogsDay) {
            iconString += `<span class="tag inspection requested">Review requested edits</span> `;
        }

        if (hosDay.isUnverifiedLogsDay) {
            iconString += `<span class="tag inspection unverified">Uncertified</span> `;
        }

        if (hosDay.isMissingAddressDay) {
            iconString += `<span class="tag inspection missingAddress">Missing location</span>`;
        }
        return iconString ? `<span class="tag-container">${ iconString }</span>` : "";
    }

    private populateEvents() {
        for (const dateHash of this.historicalDateHashes) {
            super.deleteDay(dateHash);
        }
        this.historicalDateHashes = [];

        super.populateDays();

        let currentUser = this.app.userService.getCurrentUser();
        let unverifiedDays: string[] = [];
        let requestedDays: string[] = [];
        let missingLocationDays: string[] = [];
        let daysDict = super.getDays();
        let dateHashArray = Object.keys(daysDict);

        for (let i = 0; i < dateHashArray.length; i++) {
            let dateHash = dateHashArray[i];
            let navDate = daysDict[dateHash];

            this.hosDays[dateHash] = {
                hasLogs: true
            };

            let dayItems: DutyStatusLog[] = this.app.dutyStatusLogService.getLogsByDate(this.logCollection, getLocalDaySpan(new Date(navDate.dayInMs)));
            if (dayItems.length > 0) {
                let isRequestedLogsDay: boolean | null = this.app.dutyStatusLogService.isRequestedLogsDay(currentUser, dayItems)
                    || dayItems.some(log => this.requestedAddShortenYMPCLogIdSet.has(log.id));
                let isUnverifiedLogsDay = i === 0 ? false : this.app.dutyStatusLogService.isUnverifiedLogsDay(dayItems);
                let isMissingAddressDay = this.app.dutyStatusLogService.isMissingAddressDay(dayItems);

                // Check if placeholder is a req edit. It should be the first element in the array
                // On the oldest day in the window, the placeholder may carry a requested edit from a log before the window
                if (i === super.getNumDays() - 1 && !isRequestedLogsDay && dayItems.length > 0) {
                    isRequestedLogsDay = this.app.dutyStatusLogService.isPlaceholderRequestedEdit(this.logCollection, dayItems[0]);
                }

                let dailyIssues = {
                    isRequestedLogsDay,
                    isUnverifiedLogsDay,
                    isMissingAddressDay
                };

                isUnverifiedLogsDay && unverifiedDays.push(dateHash);
                isRequestedLogsDay && requestedDays.push(dateHash);
                isMissingAddressDay && missingLocationDays.push(dateHash);

                this.hosDays[dateHash] = extend({}, this.hosDays[dateHash], dailyIssues);
            } else if (i !== 0) {
                // No logs, we don't want this day to appear on the list.
                this.deleteDay(dateHash);
            }
        }

        const userId = this.app.userService.getCurrentUser()?.id;
        const userDays = userId ? this.app.store.getState().historicalDayTracker[userId] : undefined;
        if (userDays) {
            const userHosLogsDict = userId ? this.app.dutyStatusLogService.getHosLogsAsDictionary(false, userId) : {};
            for (const dateKey of Object.keys(userDays).sort().reverse()) {
                const entry = userDays[dateKey];
                const hosLogs = entry.hosLogs.map(log => {
                    const updated = userHosLogsDict[log.id];
                    return updated ? new DutyStatusLog(updated) : new DutyStatusLog(log);
                });

                const year = parseInt(dateKey.slice(0, 4), 10);
                const month = parseInt(dateKey.slice(4, 6), 10) - 1;
                const day = parseInt(dateKey.slice(6, 8), 10);
                const dayDate = dstCalculator.toReverseOffsetDateTime(new Date(year, month, day, 0, 0, 0, 0));
                const dateHash = dayDate.toISOString();

                const isRequestedLogsDay = !!this.app.dutyStatusLogService.isRequestedLogsDay(currentUser, hosLogs);
                const isUnverifiedLogsDay = this.app.dutyStatusLogService.isUnverifiedLogsDay(hosLogs);
                const isMissingAddressDay = this.app.dutyStatusLogService.isMissingAddressDay(hosLogs);

                if (!isRequestedLogsDay && !isUnverifiedLogsDay && !isMissingAddressDay) {
                    continue;
                }

                this.hosDays[dateHash] = { hasLogs: true, isRequestedLogsDay, isUnverifiedLogsDay, isMissingAddressDay };
                const formattedDate = dayjs(dstCalculator.toLocalDateTime(dayDate), undefined, this.language).format("ddd MMM D");
                super.addDay(dateHash, formattedDate, dayDate.getTime());
                this.historicalDateHashes.push(dateHash);

                isUnverifiedLogsDay && unverifiedDays.push(dateHash);
                isRequestedLogsDay && requestedDays.push(dateHash);
                isMissingAddressDay && missingLocationDays.push(dateHash);
            }
        }

        this.unverifiedDays = unverifiedDays;
        this.requestedDays = requestedDays;
        this.missingLocationDays = missingLocationDays;
    }

    private openDatePickerIssues() {
        let earlyReturnCallBack = (dateHash: string, _1: number) => {
            let hosDay = this.hosDays[dateHash];
            if (!(hosDay && (hosDay.isMissingAddressDay || hosDay.isRequestedLogsDay || hosDay.isUnverifiedLogsDay))) {
                return true;
            }
            return false;
        };
        let getIconCallBack = (dateHash: string) => {
            let hosDay = this.hosDays[dateHash];
            return hosDay && this.getIconHtml(hosDay);
        };

        this.selectHosDay(earlyReturnCallBack, getIconCallBack);
    }

    private selectHosDay(earlyReturnCallBack?: (dateHash: string, index: number) => boolean, getIcon?: (dateHash: string) => string) {
        let requestedEditBanner = this.requestedDays.length > 0
            ? `<div class="noticeBox noticeBox--type_warning" role="alert">
                    <div class="noticeBox__container">
                        <span class="noticeBox__title"><svg class='svgIcon big bottom-margin' role='img' aria-label='Requested edit info icon'><use xlink:href='#drive-info-circled'></use></svg></span>
                        <span class="noticeBox__content">You must accept or reject logs that have been changed by an administrator before certifying outstanding days or making modifications yourself</span>
                    </div>
                </div>`
            : "";

        super.selectDay("issues-only-", "Logs to review", requestedEditBanner, earlyReturnCallBack, getIcon);
    }
}